export const ProductCard = (product, quickAdd = false) => ({
  product: typeof product === 'string' ? JSON.parse(product) : product,
  hidden: false,
  selectedOptions: [],
  sizes: '500px',
  optionsOpen: false,
  quantity: 1,
  // Computed
  get thumbnail() {
    // If current_variant has a featured media, return that, otherwise return product image
    const src =
      this.product?.images.find((img) => img.id === this?.current_variant.image_id)?.src || this.product?.image?.src;
    return {
      src,
      srcset: [550, 1100, 1445].map((w) => `${src}width=${w} ${w}w`).join(','),
    };
  },
  get current_variant() {
    return (
      this.product.variants.find((variant) => {
        const variantOptions = [variant.option1, variant.option2, variant.option3];
        return this.selectedOptions.every((option) => variantOptions.includes(option));
      }) || this.product.variants[0]
    );
  },
  get price() {
    const symbol = window.cartStrings.currencySymbol;
    return symbol + this.current_variant.price;
  },
  get priceFrom() {
    return this.product?.variants.reduce(
      (acc, cv) => (cv.price < acc ? (acc = cv.price) : acc),
      this.product.variants[0].price
    );
  },
  get hasOptions() {
    return this.product.options[0].name !== 'Title';
  },
  get soldOut() {
    return (
      this.current_variant?.inventory_quantity <= 0 &&
      this.current_variant?.inventory_management?.toLowerCase() != 'continue'
    );
  },
  formatMoney(amount) {
    if (!amount) return;
    return window.cartStrings.currencySymbol + Number(amount / 100)?.toFixed(2);
  },
  setSelectedOptions(index, name) {
    this.selectedOptions[index] = name;
  },
  getVariantFromUrl() {
    const url = new URL(location);
    const variant = url.searchParams.get('variant');
    if (!variant) return;
    const variantObj = this.product.variants.find((v) => v.id == variant);
    this.selectedOptions = [variantObj.option1, variantObj.option2, variantObj.option3];
  },
  init() {
    // If this is a quick-add ATC section, listen for when the variant changes via the main Product Variant Selector and adjust the options accordingly
    if (quickAdd) {
      this.getVariantFromUrl();

      // Listen for history changes
      document.addEventListener('variant:changed', (e) => {
        this.getVariantFromUrl();

        // Set the options
        const fieldsets = [...this.$refs.options?.querySelectorAll('fieldset')];
        this.selectedOptions.forEach((option, index) => {
          const input = fieldsets[index]?.querySelector(`input[value="${option}"]`);
          if (!input) return;
          input.checked = true;
        });
      });

      // Listen for when atctn is intersecting
      document.addEventListener('section:intersecting', (e) => {
        this.hidden = e.detail;
      });
    }

    if (!this.$refs.img) return;
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        this.sizes = entry.target.clientWidth + 'px';
      });
    });

    resizeObserver.observe(this.$refs.img);
  },
});

export const Product = (product, initialVariant, mainProduct = true) => ({
  product,
  selectedOptions: initialVariant?.options || [],
  quantity: 1,
  activeSlide: 0,
  sliderImages: null,
  get current_variant() {
    return (
      this.product.variants.find((variant) =>
        this.selectedOptions.every((option) => variant.options.includes(option))
      ) || this.product.variants[0]
    );
  },
  get amountSaved() {
    if (!this.current_variant.compare_at_price || current_variant.compare_at_price < current_variant.price) return;

    const amt = this.formatMoney(this.current_variant?.compare_at_price - this.current_variant?.price);
    // Round down to nearest whole number
    const percentage = Math.floor(100 - (this.current_variant?.price / this.current_variant?.compare_at_price) * 100);
    return `You save ${percentage}% (${amt})`;
  },
  formatMoney(amount) {
    if (!amount) return;
    return (
      window.cartStrings.currencySymbol + Number(amount / 100)?.toFixed(2)
      // .replace(/[.,]00$/, '')
    );
  },
  setSelectedOptions(index, name) {
    this.selectedOptions[index - 1] = name;
  },
  scrollToFeaturedMedia(id) {
    const featuredMedia = document.querySelector(`#media-${id || this.current_variant?.featured_media?.id}`);

    if (!featuredMedia) return;

    // Set the correct Active Slide
    const index = this.sliderImages.findIndex((img) => img === featuredMedia);
    this.activeSlide = index;

    // ScrollTo
    featuredMedia.parentElement.scrollTo({
      left: featuredMedia.offsetLeft,
      behavior: 'smooth',
    });
  },
  setZoom(e) {
    const x = ((e.offsetX / e.target.offsetWidth) * 100).toFixed(2) + '%';
    const y = ((e.offsetY / e.target.offsetHeight) * 100).toFixed(2) + '%';
    this.$refs.slider.style.setProperty('--x', x);
    this.$refs.slider.style.setProperty('--y', y);
  },
  removeZoom(e) {
    this.$refs.slider.style.setProperty('--x', '50%');
    this.$refs.slider.style.setProperty('--y', '50%');
  },
  scrollToSlide(id) {
    const slide = this.$refs.slider.querySelector(id);
    this.$refs.slider.scrollTo({
      left: slide.offsetLeft,
      behavior: 'smooth',
    });
  },
  async setActiveSlide() {
    const slider = this.$refs.slider;
    this.activeSlide = Math.floor(slider.scrollLeft / slider.clientWidth) + 1;
    // Scroll thumbnail into view
    await this.$nextTick();
    const thumbnails = document.querySelectorAll('[data-slider-thumbnails] > li');
    // Scroll parent element to the active thumbnail and center it
    thumbnails[this.activeSlide].parentElement.scrollTo({
      left:
        thumbnails[this.activeSlide].offsetLeft -
        thumbnails[this.activeSlide].parentElement.clientWidth / 2 +
        thumbnails[this.activeSlide].clientWidth / 2,
      behavior: 'smooth',
    });
  },
  getVariantFromUrl(id, options) {
    const url = new URL(location);
    const variant = url.searchParams.get('variant');
    if (!variant || variant == id) return;
    console.log(id, options);
    const variantObj = this.product.variants.find((v) => v.id == variant);
    this.selectedOptions = [variantObj.option1, variantObj.option2, variantObj.option3];
  },
  init() {
    // If used on Main product, enable Deep Linking and scroll to featured media
    if (mainProduct) {
      // Deep linking
      this.$watch('current_variant', (value) => {
        // Change URL history
        const url = new URL(location);

        url.searchParams.set('variant', value.id);
        history.pushState({}, '', url);
        // Dispatch event for flkty
        this.$dispatch('variant:changed');
      });

      // IntersectionObserver on the atcBtn ref
      const section = this.$refs.section;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.$dispatch('section:intersecting', entry.isIntersecting);
        });
      });
      observer.observe(section);
    }
  },
});
